import { useRef, useState } from "react";

import { Info } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@components/ui/tooltip";

/**
 * The ⓘ affordance next to the "Consent Confirmation" title. On hover, or on
 * keyboard focus, it surfaces the "About Informed Consent" explainer
 * (MDRS-115) — what informed consent means and why it is required. Purely
 * informational.
 *
 * The trigger is the first focusable child of the consent Dialog / Scribe
 * session header, so Radix moves focus here when either opens. The shared
 * TooltipProvider runs with delayDuration=0, so Radix's focus-open would pop
 * the tooltip open the moment the modal appeared — with no hover or click
 * (MDRS-124). We control `open` and reject an open request that comes from
 * that programmatic focus: it is not driven by the pointer and the trigger is
 * not focus-visible. Genuine hover and keyboard focus still open it.
 */
export const ConsentInfoTooltip = () => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pointerInsideRef = useRef(false);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Allow opens from a real pointer hover or a keyboard focus-visible
      // focus. Block the programmatic focus Dialog moves here on open, which
      // is neither (MDRS-124).
      const focusVisible = triggerRef.current?.matches(":focus-visible") ?? false;
      if (!pointerInsideRef.current && !focusVisible) return;
    }
    setOpen(next);
  };

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label="About informed consent"
            className="rounded-full text-primary hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            onPointerEnter={() => {
              pointerInsideRef.current = true;
            }}
            onPointerLeave={() => {
              pointerInsideRef.current = false;
            }}
          >
            <Info className="size-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="max-w-sm space-y-4 rounded-xl bg-background-white p-4 text-left text-sm text-background-dark drop-shadow-lg"
        >
          <div className="space-y-2">
            <p className="font-semibold">What is informed consent?</p>
            <div className="space-y-2 rounded-lg border border-primary-100 bg-primary-50 p-3">
              <p>
                Informed consent means the examinee has been clearly informed that
                this assessment session will be recorded and transcribed using{" "}
                <span className="font-bold">Assessment Scribe</span>.
              </p>
              <p>
                They have been given the opportunity to ask questions and have agreed
                to proceed.
              </p>
            </div>
          </div>
          <div className="space-y-1">
            <p className="font-semibold">Why it is required</p>
            <p className="opacity-80">
              Recording and transcription requires the explicit consent of the
              individual being assessed. This is a legal and ethical requirement to
              protect the rights and privacy of the examinee.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
